import React from 'react';
import PropTypes from 'prop-types';
import {
  FormattedMessage,
} from 'react-intl';
import {
  Field,
} from 'react-final-form';

import {
  IntlConsumer,
} from '@folio/stripes/core';
import {
  Col,
  Row,
  Select,
  TextArea,
  Datepicker,
} from '@folio/stripes/components';

import {
  Assignment,
} from '../../../../../../common';

import {
  TaskStatus,
} from './components';
import {
  PRIORITY,
  TASK_STATUS,
  ASSIGN_TASK_FIELDS,
  DATE_FORMAT,
} from '../../../../../../../constants';

import css from './assign-task.css';

const assignTasksPriority = ({ formatMessage }) => [
  {
    value: PRIORITY.LOW,
    label: formatMessage({ id: `ui-task-list.priority.${PRIORITY.LOW}` }),
  },
  {
    value: PRIORITY.MEDIUM,
    label: formatMessage({ id: `ui-task-list.priority.${PRIORITY.MEDIUM}` }),
  },
  {
    value: PRIORITY.HIGH,
    label: formatMessage({ id: `ui-task-list.priority.${PRIORITY.HIGH}` }),
  },
];

const assignTasksStatus = ({ formatMessage }) => [
  {
    value: TASK_STATUS.CREATED,
    label: formatMessage({ id: `ui-task-list.task.statuses.${TASK_STATUS.CREATED}` }),
  },
  {
    value: TASK_STATUS.IN_PROGRESS,
    label: formatMessage({ id: `ui-task-list.task.statuses.${TASK_STATUS.IN_PROGRESS}` }),
  },
  {
    value: TASK_STATUS.BLOCKED,
    label: formatMessage({ id: `ui-task-list.task.statuses.${TASK_STATUS.BLOCKED}` }),
  },
  {
    value: TASK_STATUS.FAILED,
    label: formatMessage({ id: `ui-task-list.task.statuses.${TASK_STATUS.FAILED}` }),
  },
  {
    value: TASK_STATUS.CANCELLED,
    label: formatMessage({ id: `ui-task-list.task.statuses.${TASK_STATUS.CANCELLED}` }),
  },
  {
    value: TASK_STATUS.ON_HOLD,
    label: formatMessage({ id: `ui-task-list.task.statuses.${TASK_STATUS.ON_HOLD}` }),
  },
  {
    value: TASK_STATUS.COMPLETED,
    label: formatMessage({ id: `ui-task-list.task.statuses.${TASK_STATUS.COMPLETED}` }),
  },
];

const AssignTask = ({
  index,
  task,
  fields,
  canAssignedTaskStatusChange,
  getTeams,
  teams,
  teamsLoaded,
  getUsers,
  users,
}) => {
  return (
    <div
      key={index}
      className={css.assignTasksRow}
    >
      <IntlConsumer>
        {intl => (
          <>
            <Row className={css.assignTasksSubRow}>
              <Col
                data-test-assign-task-name
                sm={9}
              >
                <Field
                  required
                  component={TextArea}
                  id={`${ASSIGN_TASK_FIELDS.NAME}${index}`}
                  label={<FormattedMessage id="ui-task-list.job.assignTasks.field.name" />}
                  name={`${task}.${ASSIGN_TASK_FIELDS.NAME}`}
                  type="text"
                />
              </Col>
              <Col sm={3}>
                <Row>
                  <Col
                    data-test-assign-task-start-date
                    sm={6}
                  >
                    <Field
                      component={Datepicker}
                      dateFormat={DATE_FORMAT}
                      id={`${ASSIGN_TASK_FIELDS.START_DATE}${index}`}
                      label={<FormattedMessage id="ui-task-list.job.assignTasks.field.startDate" />}
                      name={`${task}.${ASSIGN_TASK_FIELDS.START_DATE}`}
                    />
                  </Col>
                  <Col
                    data-test-assign-task-due-date
                    sm={6}
                  >
                    <Field name={`${task}.${ASSIGN_TASK_FIELDS.DUE_DATE}`}>
                      {({ input, meta }) => (
                        <Datepicker
                          useInput
                          usePortal
                          dateFormat={DATE_FORMAT}
                          error={meta.error}
                          id={`${ASSIGN_TASK_FIELDS.DUE_DATE}${index}`}
                          input={input}
                          label={<FormattedMessage id="ui-task-list.job.assignTasks.field.dueDate" />}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className={css.assignTasksSubRow}>
              <Col
                data-test-assign-task-description
                sm={9}
              >
                <Field
                  component={TextArea}
                  id={`${ASSIGN_TASK_FIELDS.DESCRIPTION}${index}`}
                  label={<FormattedMessage id="ui-task-list.job.assignTasks.field.description" />}
                  name={`${task}.${ASSIGN_TASK_FIELDS.DESCRIPTION}`}
                  type="text"
                />
              </Col>
              <Col sm={3}>
                <Row>
                  <Col
                    data-test-assign-task-priority
                    sm={6}
                  >
                    <Field
                      component={Select}
                      dataOptions={assignTasksPriority(intl)}
                      id={`${ASSIGN_TASK_FIELDS.PRIORITY}${index}`}
                      label={<FormattedMessage id="ui-task-list.job.assignTasks.field.priority" />}
                      name={`${task}.${ASSIGN_TASK_FIELDS.PRIORITY}`}
                      type="text"
                    />
                  </Col>
                  <Col
                    data-test-assign-task-status
                    sm={6}
                  >
                    { !canAssignedTaskStatusChange &&
                      <TaskStatus
                        index={index}
                        statusTranslationKey={`ui-task-list.task.statuses.${fields.value[index][ASSIGN_TASK_FIELDS.STATUS]}`}
                      />
                    }
                    { canAssignedTaskStatusChange &&
                      <Field
                        component={Select}
                        dataOptions={assignTasksStatus(intl)}
                        id={`${ASSIGN_TASK_FIELDS.STATUS}${index}`}
                        label={<FormattedMessage id="ui-task-list.job.assignTasks.field.status" />}
                        name={`${task}.${ASSIGN_TASK_FIELDS.STATUS}`}
                        type="text"
                      />
                    }
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className={css.assignTasksSubRow}>
              <Col
                data-test-assign-task-assign-user
                sm={12}
              >
                <Assignment
                  fields={fields}
                  index={index}
                  task={task}
                  teams={teams}
                  teamsLoaded={teamsLoaded}
                  users={users}
                  getTeams={getTeams}
                  getUsers={getUsers}
                />
              </Col>
            </Row>
          </>
        )}
      </IntlConsumer>
    </div>
  );
};

AssignTask.propTypes = {
  canAssignedTaskStatusChange: PropTypes.bool.isRequired,
  fields: PropTypes.shape({
    value: PropTypes.array.isRequired,
  }).isRequired,
  getTeams: PropTypes.func.isRequired,
  getUsers: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  task: PropTypes.string.isRequired,
  teams: PropTypes.array.isRequired,
  teamsLoaded: PropTypes.bool.isRequired,
  users: PropTypes.object.isRequired,
};

export default AssignTask;
